import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { canManageOrganizations, canInviteUsers, getRoleBadgeColor } from "@/mocks/auth";
import { api } from "@/api/client";
import type { OrganizationRead, UserRead } from "@/api/types";
import { useAuth } from "@/contexts/AuthContext";

const roles = ["Government Health Officer", "Organization User", "Admin", "Viewer"] as const;

export default function Organizations() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const displayUser = user!;

  const [orgs, setOrgs] = useState<OrganizationRead[]>([]);
  const [users, setUsers] = useState<UserRead[]>([]);
  const [loading, setLoading] = useState(true);

  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<"users" | "activity">("users");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: "", email: "", role: "Viewer" as typeof roles[number] });
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  const isAdmin = canManageOrganizations(displayUser.role);
  const canInvite = canInviteUsers(displayUser.role);

  // Load orgs + users in parallel
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [orgData, userData] = await Promise.all([
          api.organizations() as Promise<OrganizationRead[]>,
          api.settingsUsers() as Promise<UserRead[]>,
        ]);
        if (!cancelled) {
          const orgList = orgData ?? [];
          setOrgs(orgList);
          setUsers(userData ?? []);
          if (orgList.length > 0) setSelectedOrgId(orgList[0].id);
        }
      } catch {
        if (!cancelled) { setOrgs([]); setUsers([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const selectedOrg = orgs.find((o) => o.id === selectedOrgId) ?? null;

  const orgMembers = useMemo(
    () => users.filter((u) => u.org_id === selectedOrgId),
    [users, selectedOrgId]
  );

  function showToast(message: string, type: "success" | "error" = "success") {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }

  async function handleRoleChange(userId: number, newRole: string) {
    try {
      await api.patchSettingsUser(userId, { role: newRole });
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role: newRole } : u)));
      showToast(`${t("settings_save")} → ${newRole}`);
    } catch {
      showToast(t("common_error") ?? "Error updating role", "error");
    }
  }

  function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!inviteForm.name || !inviteForm.email) return;
    setInviteOpen(false);
    setInviteForm({ name: "", email: "", role: "Viewer" });
    showToast(`${t("org_send_invitation")} ${inviteForm.email}`);
  }

  if (loading) {
    return (
      <div className="min-h-[200px] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-slate-700 border-t-teal rounded-full animate-spin" />
      </div>
    );
  }

  if (!selectedOrg) return null;

  return (
    <div className="animate-fade-in space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-display font-bold text-slate-900 dark:text-white">{t("org_title")}</h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{t("org_subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500 dark:text-slate-500">{t("settings_your_role")}</span>
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(displayUser.role)}`}>
            {displayUser.role}
          </span>
        </div>
      </div>

      {/* Organisation selector */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-xs text-slate-500 dark:text-slate-400 mb-2 uppercase tracking-wider font-semibold">
              {t("org_active_org")}
            </label>
            <select
              value={selectedOrgId ?? ""}
              onChange={(e) => setSelectedOrgId(Number(e.target.value))}
              className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2.5 text-sm text-slate-900 dark:text-white outline-none focus:border-teal"
            >
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 flex-1">
            <div className="rounded-lg bg-slate-50 dark:bg-slate-800/60 p-3">
              <p className="text-[10px] text-slate-500 dark:text-slate-500 uppercase tracking-wider">{t("org_type")}</p>
              <p className="text-sm font-medium text-slate-900 dark:text-white mt-0.5">{selectedOrg.type}</p>
            </div>
            <div className="rounded-lg bg-slate-50 dark:bg-slate-800/60 p-3">
              <p className="text-[10px] text-slate-500 dark:text-slate-500 uppercase tracking-wider">{t("org_users_short")}</p>
              <p className="text-sm font-medium text-slate-900 dark:text-white mt-0.5">{orgMembers.length} {t("org_active_suffix")}</p>
            </div>
            <div className="rounded-lg bg-slate-50 dark:bg-slate-800/60 p-3">
              <p className="text-[10px] text-slate-500 dark:text-slate-500 uppercase tracking-wider">{t("settings_key_created")}</p>
              <p className="text-sm font-medium text-slate-900 dark:text-white mt-0.5">{selectedOrg.created_at.split("T")[0]}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Organisation detail card */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-teal-muted/30">
            <i className="ri-building-2-line text-teal text-lg" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{selectedOrg.name}</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">{selectedOrg.type} {t("org_type")}</p>
          </div>
          <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/20 text-emerald border border-emerald/30">
            {t("common_active")}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 p-1 bg-slate-100 dark:bg-slate-900/60 rounded-xl border border-slate-200 dark:border-slate-800 w-fit">
        {(["users", "activity"] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === tab
                ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm"
                : "text-slate-500 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
            }`}
          >
            {tab === "users" ? t("org_team_members") : t("org_activity_logs")}
            <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
              {tab === "users" ? orgMembers.length : 0}
            </span>
          </button>
        ))}
      </div>

      {activeTab === "users" && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-800">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{t("org_team_members")}</h3>
            {canInvite && (
              <button
                onClick={() => setInviteOpen(true)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-teal text-slate-950 text-xs font-semibold hover:bg-teal-hover transition-colors whitespace-nowrap"
              >
                <i className="ri-user-add-line text-xs" />
                {t("org_invite_user")}
              </button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800">
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-wider">{t("org_user_header")}</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-wider">{t("org_role_header")}</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-wider">{t("org_status_header")}</th>
                  {isAdmin && <th className="px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-wider">{t("org_actions_header")}</th>}
                </tr>
              </thead>
              <tbody>
                {orgMembers.map((u) => (
                  <tr key={u.id} className="border-b border-slate-200/50 dark:border-slate-800/60 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-full bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-sm font-semibold">
                          {(u.full_name || u.email).split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-900 dark:text-white">{u.full_name || "—"}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-500">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${getRoleBadgeColor(u.role as any)}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-xs ${u.is_active ? "text-emerald" : "text-slate-500 dark:text-slate-500"}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${u.is_active ? "bg-emerald" : "bg-slate-400 dark:bg-slate-600"}`} />
                        {u.is_active ? t("common_active") : t("common_inactive") ?? "Inactive"}
                      </span>
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-3">
                        <select
                          value={u.role}
                          onChange={(e) => handleRoleChange(u.id, e.target.value)}
                          className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-md px-2 py-1 text-xs text-slate-700 dark:text-slate-300 outline-none focus:border-teal"
                        >
                          {roles.map((r) => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                      </td>
                    )}
                  </tr>
                ))}
                {orgMembers.length === 0 && (
                  <tr>
                    <td colSpan={isAdmin ? 4 : 3} className="px-4 py-8 text-center text-slate-500 dark:text-slate-500 text-sm">
                      {t("org_no_logs")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "activity" && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/80 p-6 text-center">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 mx-auto mb-3">
            <i className="ri-history-line text-xl text-slate-400 dark:text-slate-500" />
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400">{t("org_no_logs")}</p>
          <p className="text-xs text-slate-400 dark:text-slate-600 mt-1">Activity log endpoint coming soon</p>
        </div>
      )}

      {/* Invite modal */}
      {inviteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-800">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{t("org_invite_title")}</h3>
              <button
                onClick={() => setInviteOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 dark:text-slate-500 hover:text-slate-800 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <i className="ri-close-line" />
              </button>
            </div>
            <form onSubmit={handleInvite} className="p-5 space-y-4">
              <div>
                <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1.5">{t("org_full_name")}</label>
                <input
                  type="text"
                  required
                  value={inviteForm.name}
                  onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                  className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2.5 text-sm text-slate-900 dark:text-white outline-none focus:border-teal"
                  placeholder="Dr. Jane Smith"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1.5">{t("auth_email")}</label>
                <input
                  type="email"
                  required
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2.5 text-sm text-slate-900 dark:text-white outline-none focus:border-teal"
                  placeholder="jane.smith@organization.org"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 dark:text-slate-400 mb-1.5">{t("org_role_header")}</label>
                <select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value as typeof roles[number] })}
                  className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2.5 text-sm text-slate-900 dark:text-white outline-none focus:border-teal"
                >
                  {roles.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <p className="text-[10px] text-slate-500 dark:text-slate-500 mt-1.5">{t("org_role_desc_viewer")}</p>
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setInviteOpen(false)}
                  className="px-4 py-2 rounded-lg text-sm text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white transition-colors"
                >
                  {t("common_cancel")}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-teal text-slate-950 text-sm font-semibold hover:bg-teal-hover transition-colors"
                >
                  {t("org_send_invitation")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-5 right-5 z-50 px-4 py-3 rounded-xl shadow-lg border text-sm font-medium animate-slide-up ${
          toast.type === "success"
            ? "bg-white dark:bg-slate-900 border-teal/30 text-teal"
            : "bg-white dark:bg-slate-900 border-red-500/30 text-red-500 dark:text-red-400"
        }`}>
          <div className="flex items-center gap-2">
            <i className={toast.type === "success" ? "ri-check-line" : "ri-error-warning-line"} />
            {toast.message}
          </div>
        </div>
      )}
    </div>
  );
}
